import React, { useEffect, useState } from 'react';
import HeadingWithDetails from '../../../components/HeadingWithDetails';
import { useBranding } from '../../../branding/BrandingContextProvider';
import Breadcrumb from '../../../components/Breadcrumb';
import { PATHS, USER_ROLES } from '../../../common/constants';

import ServiceRequestStatusUpdates from '../ServiceRequestStatusUpdates';
import { isRole } from '../../../helpers/permissions';
import { Link } from 'react-router-dom';
import { convertTime } from '../../../helpers/time';
import Card from '../../../components/Card';
import Tenant from '../../../components/Tenant';
import Avatar from '../../../components/Avatar';
import Icon from '../../../components/Icon';
import Button from '../../../components/Button';
import { getAllowedTabList } from '../../../services/client';

export const ServiceRequestDetailHeader = ({
  serviceRequest,
  user,
  location,
  onEditServiceRequest,
  onDeleteServiceRequest,
  onDownloadPdf,
  navigateTo,
  handleStatusChange,
}) => {
  const { branding } = useBranding();
  const propertyViewCheck = isRole(
    [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.PROPERTY_DEVELOPER],
    user
  );
  const showLink = isRole(
    [
      USER_ROLES.ADMIN,
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PROPERTY_DEVELOPER,
      USER_ROLES.STRATA_MANAGER,
    ],
    user
  );

  const [homeOwnerDataVisibility, setHomeOwnerDataVisibility] = useState(false);

  useEffect(() => {
    let active = true;
    getAllowedTabList(serviceRequest.propertyId).then((response) => {
      if (!active) return;
      if (response.status === 200) {
        response.json().then(({ data }) => {
          if (!active || !data.length) return;
          setHomeOwnerDataVisibility(
            data.some((p) => p.name === 'Home Owner Data Visibility')
          );
        });
      }
    });
    return () => {
      active = false;
    };
  }, [serviceRequest.propertyId]);

  const shouldHideHomeOwner =
    isRole([USER_ROLES.SERVICE_PROVIDER, USER_ROLES.VENDOR], user) &&
    !homeOwnerDataVisibility;

  const shouldHideReporter =
    serviceRequest?.suite?.homeOwner?.id === serviceRequest?.reporter?.id &&
    shouldHideHomeOwner;

  return (
    <HeadingWithDetails
      status={serviceRequest.status.name}
      breadcrumb={
        isRole(USER_ROLES.HOME_OWNER, user) ? (
          <Breadcrumb to={PATHS.DASHBOARD} label="Dashboard" />
        ) : (
          <Breadcrumb
            to={location?.state?.navigateTo ?? PATHS.SERVICE_REQUESTS_LIST}
            label={branding.text.serviceRequest + 's'}
          />
        )
      }
      toast={
        isRole(
          [USER_ROLES.HOME_OWNER, USER_ROLES.REALTOR, USER_ROLES.OCCUPANT],
          user
        ) ? null : (
          <ServiceRequestStatusUpdates
            serviceRequest={serviceRequest}
            handleStatusChange={(statusFormData, serviceRequestId) =>
              handleStatusChange(statusFormData, serviceRequestId)
            }
          />
        )
      }
      headline={serviceRequest.ticketNumber ?? serviceRequest.ticketId}
      filedIn={
        <>
          <Link
            to={
              propertyViewCheck && {
                pathname: PATHS.PROPERTIES_VIEW.replace(
                  ':id',
                  serviceRequest.suite.property.id
                ),
                state: {
                  data: {
                    id: serviceRequest.suite.property.id,
                    serviceRequestId: serviceRequest.id,
                  },
                },
              }
            }
            className="serviceRequestDetails__links"
            style={{
              textDecoration: propertyViewCheck ? 'underline' : 'none',
              cursor: propertyViewCheck ? 'pointer' : 'initial',
            }}
          >
            {serviceRequest.suite.property.name}
          </Link>{' '}
          /{' '}
          <Link
            to={
              propertyViewCheck && {
                pathname: PATHS.SUITES_VIEW.replace(
                  ':id',
                  serviceRequest.id +
                    '&' +
                    serviceRequest.suite.id +
                    '&' +
                    serviceRequest.suite.property.id
                ),
                state: {
                  data: {
                    id: serviceRequest.suite.id,
                    serviceRequestId: serviceRequest.id,
                  },
                  suite: { id: serviceRequest.suite.id },
                  property: { id: serviceRequest.suite.property.id },
                  from: isRole(USER_ROLES.HOME_OWNER, user)
                    ? 'Dashboard'
                    : null,
                },
              }
            }
            className="serviceRequestDetails__links"
            style={{
              textDecoration: propertyViewCheck ? 'underline' : 'none',
              cursor: propertyViewCheck ? 'pointer' : 'initial',
            }}
          >
            {serviceRequest.suite.number}
          </Link>{' '}
          /{' '}
          <text
            className="serviceRequestDetails__links"
            style={{ textDecoration: 'none' }}
          >
            {serviceRequest.asset.name}
          </text>
          <div style={{ padding: 10, paddingLeft: 0 }}>
            Warranty start:{' '}
            <span style={{ color: 'black' }}>
              {convertTime(serviceRequest.asset.warrantyStartDate, 'date')}
            </span>
          </div>
          {serviceRequest.suite.warrantyAddress && (
            <div style={{ paddingTop: 0, paddingLeft: 0 }}>
              Warranty/Civic Address:
              <span style={{ color: 'black' }}>
                {' '}
                {serviceRequest.suite.warrantyAddress}
              </span>
            </div>
          )}
        </>
      }
      items={[
        !shouldHideReporter && (
          <Card
            title="Reporter"
            body={
              <Tenant
                headline={
                  showLink === true ? (
                    <a
                      href={PATHS.CONTACTS_VIEW.replace(
                        ':id',
                        `${serviceRequest.reporter.id}&${serviceRequest.id}`
                      )}
                    >
                      {serviceRequest.reporter.name}
                    </a>
                  ) : (
                    serviceRequest.reporter.name
                  )
                }
                email={serviceRequest.reporter.email}
                phone={serviceRequest.reporter.phone}
                strapline={serviceRequest.reporter.role}
                avatar={<Avatar url={serviceRequest.reporter.profileImage} />}
              />
            }
          />
        ),
        serviceRequest.suite.homeOwner.id !== serviceRequest.reporter.id &&
          !shouldHideHomeOwner && (
            <Card
              title="Primary Home Owner"
              body={
                <Tenant
                  headline={serviceRequest.suite.homeOwner.name}
                  email={serviceRequest.suite.homeOwner.email}
                  phone={serviceRequest.suite.homeOwner.phone}
                  strapline={serviceRequest.suite.homeOwner.role}
                  avatar={
                    <Avatar url={serviceRequest.suite.homeOwner.profileImage} />
                  }
                />
              }
            />
          ),
        <Card
          title="Assigned To"
          body={
            serviceRequest.serviceProvider.name ? (
              <Tenant
                headline={
                  showLink ? (
                    <Link
                      to={{
                        pathname: PATHS.CONTACTS_VIEW.replace(
                          ':id',
                          serviceRequest.serviceProvider.id +
                            '&' +
                            serviceRequest.id
                        ),
                        state: { navigateTo: navigateTo },
                      }}
                    >
                      {serviceRequest.serviceProvider.name}
                    </Link>
                  ) : (
                    serviceRequest.serviceProvider.name
                  )
                }
                strapline={serviceRequest.serviceProvider.role}
                avatar={
                  <Avatar
                    url={serviceRequest.serviceProvider.profileImage}
                    badge="service-provider"
                  />
                }
              />
            ) : (
              <div>No service provider assigned</div>
            )
          }
        />,
        <Card
          title="Appointment"
          body={
            <div className="serviceRequest__appointment">
              <div className="serviceRequest__appointmentIcon">
                <Icon glyph="calendar" weight="regular" />
              </div>
              <div>
                {serviceRequest.appointment.id ===
                '00000000-0000-0000-0000-000000000000'
                  ? 'No Appointment'
                  : convertTime(
                      serviceRequest.appointment.scheduledAt,
                      'dddd MM/DD/YYYY'
                    )}
                <br />
                {serviceRequest.appointment.id ===
                '00000000-0000-0000-0000-000000000000'
                  ? ''
                  : convertTime(
                      serviceRequest.appointment.scheduledAt,
                      'h:mm A'
                    )}
              </div>
            </div>
          }
        />,
      ]}
      actions={
        isRole(
          [
            USER_ROLES.PROPERTY_DEVELOPER,
            USER_ROLES.SUPER_ADMIN,
            USER_ROLES.ADMIN,
          ],
          user
        ) && (
          <>
            <Button
              color="semantic"
              onClick={() => onEditServiceRequest()}
              label={<Icon glyph="pencil" weight="light" />}
            />
            <Button
              color="semantic"
              onClick={() =>
                window.confirm(
                  `Are you sure you want to delete ${serviceRequest.ticketId}?`
                ) && onDeleteServiceRequest()
              }
              label={<Icon glyph="trash" weight="light" />}
            />
            <Button
              color="semantic"
              onClick={() => onDownloadPdf()}
              label={<Icon glyph="download" weight="light" />}
            />
          </>
        )
      }
    />
  );
};
